import API from "../../api/Api.service";

const getChats = async () => {
  const res = await API.get("/chats");
  return Array.isArray(res.data) ? res.data : [];
};

const getMessagesByChatId = async (chatId, page = 1, pageSize = 50) => {
  const res = await API.get("/messages_by_chat_id", {
    params: {
      chat_id: chatId,
      page,
      page_size: pageSize,
    },
  });

  return Array.isArray(res.data)
    ? {
        items: res.data,
        page,
        page_size: pageSize,
        total: res.data.length,
      }
    : res.data;
};

const sendMessage = async (payload) => {
  const res = await API.post("/send_message", payload);
  return res.data;
};

const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append("file", file);

  const res = await API.post("/upload_file", formData, {
    timeout: 30000,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return res.data;
};

const getAiResponse = async (payload) => {
  const res = await API.post("/ai_message", payload);
  return res.data;
};

const muteChat = async (chatId) => {
  const res = await API.post(`/mute_chat/${chatId}`);
  return res.data;
};

const unmuteChat = async (chatId) => {
  const res = await API.post(`/unmute_chat/${chatId}`);
  return res.data;
};

const deleteChat = async (chatId) => {
  const res = await API.post(`/delete_chat/${chatId}`);
  return res.data;
};

const pinChat = async (chatId) => {
  const res = await API.post(`/pin_chat/${chatId}`);
  return res.data;
};

const unpinChat = async (chatId) => {
  const res = await API.post(`/unpin_chat/${chatId}`);
  return res.data;
};

const createChat = async (username) => {
  const res = await API.post("/create_chat", { username });
  return res.data;
};

const blockUser = async (username) => {
  const res = await API.post(`/users/${encodeURIComponent(username)}/block`);
  return res.data;
};

const createAiChat = async () => {
  const res = await API.post("/create_ai_chat");
  return res.data;
};

const createGroupChat = async ({ name, members }) => {
  const res = await API.post("/create_group_chat", {
    name,
    members,
  });
  return res.data;
};

const getChatFriends = async () => {
  const res = await API.get("/chat_friends");
  return (Array.isArray(res.data) ? res.data : []).map((friend) => ({
    ...friend,
    full_name:
      friend.full_name ??
      friend.chatName ??
      friend.display_name ??
      friend.username,
    profile_picture:
      friend.profile_picture ?? friend.avatarUrl ?? friend.avatar_url ?? null,
  }));
};

// Media galleries. The web previously derived the media/files/posts tabs purely from the
// messages already paged into memory; these read the full chat history from the backend.
const getChatMedia = async (chatId, { type, page = 1, limit = 40 } = {}) => {
  const params = { page, limit };
  if (type) {
    params.type = type;
  }
  const res = await API.get(`/chats/${chatId}/media`, { params });
  return res.data && Array.isArray(res.data.media) ? res.data : { media: [] };
};

const getChatMediaStats = async (chatId) => {
  const res = await API.get(`/chats/${chatId}/media/stats`);
  return res.data || { imagesAndVideos: 0, sharedPosts: 0, files: 0 };
};

const getSharedPosts = async (chatId, { page = 1, limit = 20 } = {}) => {
  const res = await API.get(`/chats/${chatId}/shared-posts`, {
    params: { page, limit },
  });
  return res.data && Array.isArray(res.data.posts) ? res.data : { posts: [] };
};

// In-conversation message search. Server-side (whole history) — replaces the former
// client-side filter that could only see messages already paged into memory.
const searchMessages = async (
  chatId,
  query,
  { limit = 50, offset = 0 } = {},
) => {
  const res = await API.get("/search/messages", {
    params: { chat_id: chatId, q: query, limit, offset },
  });
  return Array.isArray(res.data) ? res.data : [];
};

const getChatMembers = async (chatId) => {
  const res = await API.get(`/chats/${chatId}/members`);
  const data = res.data;

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.members)) {
    return data.members;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  return [];
};

// Group membership. These persist to the backend (the web previously only
// mutated local state, so group edits vanished on reload).
const addGroupMember = async (chatId, username) => {
  const res = await API.post(`/chats/${chatId}/members`, { username });
  return res.data;
};

const removeGroupMember = async (chatId, username) => {
  const res = await API.delete(
    `/chats/${chatId}/members/${encodeURIComponent(username)}`,
  );
  return res.data;
};

const leaveGroup = async (chatId) => {
  const res = await API.post(`/chats/${chatId}/leave`);
  return res.data;
};

const ChatService = {
  getChats,
  getMessagesByChatId,
  sendMessage,
  uploadFile,
  getAiResponse,
  muteChat,
  unmuteChat,
  deleteChat,
  pinChat,
  unpinChat,
  createChat,
  blockUser,
  createAiChat,
  createGroupChat,
  getChatFriends,
  getChatMembers,
  getChatMedia,
  getChatMediaStats,
  getSharedPosts,
  searchMessages,
  addGroupMember,
  removeGroupMember,
  leaveGroup,
};

export default ChatService;
